import { Bar } from "react-chartjs-2";
import "chart.js/auto";
import { getColorByValue } from "../../../helpers/utils";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { useTranslation } from "react-i18next";
import { ChartOptions } from "chart.js/auto";
import { ActivityData, PercentageHistogram } from "../../../types/chartData";
import { forwardRef } from "react";
import { ChartJSOrUndefined } from "react-chartjs-2/dist/types";

type PercChangeHistogramChartProps = {
  height: string;
  data1: PercentageHistogram | null;
  data2: PercentageHistogram | null;
  attribute: string;
  onRendered?: () => void;
};

const PercChangeHistogramChart = forwardRef<
  ChartJSOrUndefined<"bar", { x: number; y: number }[], unknown>,
  PercChangeHistogramChartProps
>(({ height, data1, data2, attribute, onRendered }, ref) => {
  const { t } = useTranslation();
  const histogramData1 =
    attribute === "MLI"
      ? data1?.mli
      : data1?.pain;
  const histogramData2 =
    attribute === "MLI"
      ? data2?.mli
      : data2?.pain;

  const histogramData: number[] = histogramData2
    ? histogramData2.map((element, index) =>
        histogramData1 ? element - (histogramData1[index] ?? 0) : element
      )
    : [];

  const max: number = Math.max(...histogramData);
  const min = Math.min(...histogramData);

  const labels = Array.from({ length: 10 }, (_, i) => i + 0.5);
  const _data = histogramData?.map((value: number, index: number) => ({
    x: labels[index],
    y: value,
  }));

  // Prepare the data for Chart.js
  const data = {
    datasets: [
      {
        label: attribute,
        data: _data,
        backgroundColor: labels.map((_, i) =>
          getColorByValue(i, attribute === "MLI" ? "MLI" : "pain", true)
        ),
        borderColor: labels.map((_, i) =>
          getColorByValue(i, attribute === "MLI" ? "MLI" : "pain", true)
        ),
        borderWidth: 1,
        barPercentage: 1,
        categoryPercentage: 1,
      },
    ],
  };

  const options: ChartOptions<"bar"> = {
    maintainAspectRatio: false,
    scales: {
      x: {
        type: "linear",
        offset: false,
        grid: {
          offset: false,
        },
        ticks: {
          stepSize: 1,
        },
        title: {
          display: true,
          text: attribute,
        },
      },
      y: {
        //beginAtZero: true,
        max: max + 5,
        min: min >= 0 ? 0 : min - 5,
        title: {
          display: false,
          text: t("decrease-increase"),
        },
      },
    },
    animation: {
      onComplete: () => {
        if (typeof onRendered === "function") {
          onRendered();
        }
      },
    },
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: true,
        text: `   ${attribute} ${t("range-percentage")}`,
        align: "start",
        padding: {
          top: 5,
          bottom: 15,
        },
      },
      datalabels: {
        anchor: "end",
        align: "end",
        formatter: (_: any, context: any) => {
          const y = context.chart.data.datasets[0].data[context.dataIndex].y;
          return y === 0 ? "" : y > 0 ? `+${y}%` : `${y}%`;
        },
      },
      tooltip: {
        callbacks: {
          title: (items) => {
            if (!items.length) {
              return "";
            }
            const item = items[0];
            const x = item.parsed.x;
            const labelMin = x - 0.5;
            const labelMax = x + 0.5;
            return `${labelMin} - ${labelMax}`;
          },
        },
      },
    },
  };

  return (
    <div
      style={{
        height: height,
        width: "99%",
      }}
    >
      <Bar
        ref={ref}
        plugins={[ChartDataLabels]}
        data={data}
        options={options}
      />
    </div>
  );
});

export default PercChangeHistogramChart;
